import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  LinkOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  PlusOutlined,
  DeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { ExperimentOutlined } from '@ant-design/icons';
import {
  Table,
  Select,
  InputNumber,
  Switch,
  Button,
  Space,
  Input,
  Modal,
  Checkbox,
  message,
  Spin,
  Tag,
  DatePicker,
  Popconfirm,
  Tooltip,
  Alert,
} from 'antd';
import dayjs from 'dayjs';
import { ROUTES, OPERATION } from '@/constants';
import * as ozService from '@/services/payrollEngine.service';
import StatusTimelineModal from './_shared/StatusTimelineModal';
import {
  useEmployeeProfiles,
  useUpdateEmployeeProfile,
  useBulkUpdateProfiles,
} from './_shared/hooks';

const CALC_TYPE_KEYS = [
  { value: 'TYPE_01_FIXED_RSD', key: 'ozCalcType01' },
  { value: 'TYPE_02_POINTS', key: 'ozCalcType02' },
  { value: 'TYPE_05_POINTS_INPUT', key: 'ozCalcType05' },
  { value: 'TYPE_07_ADVANCE', key: 'ozCalcType07' },
  { value: 'TYPE_08_POINTS_NO_HOURS', key: 'ozCalcType08' },
  { value: 'TYPE_11_PREV_MONTH', key: 'ozCalcType11' },
  { value: 'TYPE_40_AVG_3M', key: 'ozCalcType40' },
  { value: 'TYPE_41_AVG_CONTRACT', key: 'ozCalcType41' },
  { value: 'TYPE_47_MATERNITY_12M', key: 'ozCalcType47' },
  { value: 'TYPE_62_BASE_LIMIT', key: 'ozCalcType62' },
];

const WORK_TIME_OPTIONS = [
  { value: 100, label: '100%' },
  { value: 75, label: '75%' },
  { value: 50, label: '50%' },
  { value: 25, label: '25%' },
];

// Profile considered "complete" if calc-type-mandatory fields are set.
// Used by status indicator column to flag incomplete configs.
function profileCompleteness(p) {
  // Other income (outside employment) — complete if it has a contractType;
  // salary fields (calcType/points) do not apply.
  if (p?.incomeRegime === 'otherIncome') {
    return p?.otherIncome?.contractType
      ? { ok: true, missing: [] }
      : { ok: false, missing: ['contractType'] };
  }
  if (!p?.calcType) return { ok: false, missing: ['calcType'] };
  const missing = [];
  if (p.calcType === 'TYPE_01_FIXED_RSD' && !p.fixedGross && !p.fixedNet) {
    missing.push('fixedGross/fixedNet');
  }
  if (
    (p.calcType === 'TYPE_02_POINTS' ||
      p.calcType === 'TYPE_05_POINTS_INPUT' ||
      p.calcType === 'TYPE_08_POINTS_NO_HOURS') &&
    !p.points
  ) {
    missing.push('points');
  }
  if (p.calcType === 'TYPE_41_AVG_CONTRACT' && !p.contractAvgGross) {
    missing.push('contractAvgGross');
  }
  return { ok: missing.length === 0, missing };
}

function ActiveDeductionsModal({ open, onClose, value, onSave, t }) {
  const [rows, setRows] = useState(value || []);
  // Re-init when modal reopens with fresh data
  useMemo(() => {
    if (open) setRows(value || []);
  }, [open, value]);

  const update = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const add = () =>
    setRows((prev) => [
      ...prev,
      {
        deductionCode: '',
        amount: 0,
        startDate: null,
        endDate: null,
        remainingTotal: null,
        note: '',
      },
    ]);
  const remove = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  return (
    <Modal
      title={t('ozActiveDeductions')}
      open={open}
      onCancel={onClose}
      width={900}
      onOk={() => onSave(rows)}
      okText={t('save')}
      cancelText={t('cancel')}
    >
      <div className="mb-3 flex justify-between text-xs text-gray-500">
        <span>{t('ozActiveDeductionsHint')}</span>
        <Button size="small" icon={<PlusOutlined />} onClick={add}>
          {t('ozActiveDeductionAdd')}
        </Button>
      </div>
      <Table
        size="small"
        rowKey={(_, idx) => idx}
        dataSource={rows}
        pagination={false}
        columns={[
          {
            title: t('code'),
            dataIndex: 'deductionCode',
            width: 90,
            render: (v, _, idx) => (
              <Input
                size="small"
                value={v}
                onChange={(e) => update(idx, { deductionCode: e.target.value })}
              />
            ),
          },
          {
            title: t('ozActiveDeductionAmount'),
            dataIndex: 'amount',
            width: 110,
            render: (v, _, idx) => (
              <InputNumber
                size="small"
                value={v}
                min={0}
                onChange={(val) => update(idx, { amount: val })}
                style={{ width: 100 }}
              />
            ),
          },
          {
            title: t('ozActiveDeductionStart'),
            dataIndex: 'startDate',
            width: 130,
            render: (v, _, idx) => (
              <DatePicker
                size="small"
                value={v ? dayjs(v) : null}
                onChange={(d) =>
                  update(idx, { startDate: d ? d.toISOString() : null })
                }
              />
            ),
          },
          {
            title: t('ozActiveDeductionEnd'),
            dataIndex: 'endDate',
            width: 130,
            render: (v, _, idx) => (
              <DatePicker
                size="small"
                value={v ? dayjs(v) : null}
                onChange={(d) =>
                  update(idx, { endDate: d ? d.toISOString() : null })
                }
              />
            ),
          },
          {
            title: t('ozActiveDeductionRemaining'),
            dataIndex: 'remainingTotal',
            width: 110,
            render: (v, _, idx) => (
              <InputNumber
                size="small"
                value={v}
                min={0}
                onChange={(val) => update(idx, { remainingTotal: val })}
                style={{ width: 100 }}
              />
            ),
          },
          {
            title: t('ozActiveDeductionNote'),
            dataIndex: 'note',
            render: (v, _, idx) => (
              <Input
                size="small"
                value={v}
                onChange={(e) => update(idx, { note: e.target.value })}
              />
            ),
          },
          {
            title: '',
            key: 'actions',
            width: 40,
            render: (_, __, idx) => (
              <Popconfirm
                title={t('confirmDelete')}
                onConfirm={() => remove(idx)}
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            ),
          },
        ]}
      />
    </Modal>
  );
}

// Generički per-godMes map editor (procMR %, unusedGoHours sati…).
function AbsoftGodMesMapModal({
  open,
  onClose,
  value,
  onSave,
  t,
  title,
  hint,
  keyLabel,
  valueLabel = '%',
  keyKind = 'godMes', // 'godMes' | 'codeGodMes' | 'code'
}) {
  // value is a Map-like object { '202604': 2.8, ... }; convert to array
  // [{godMes, value}] for editing, then back on save.
  const [rows, setRows] = useState(() =>
    Object.entries(value || {}).map(([godMes, val]) => ({ godMes, value: val }))
  );
  useMemo(() => {
    if (open) {
      setRows(
        Object.entries(value || {}).map(([godMes, val]) => ({
          godMes,
          value: val,
        }))
      );
    }
  }, [open, value]);

  const update = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const add = () => setRows((prev) => [...prev, { godMes: '', value: 0 }]);
  const remove = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  // A malformed key used to save silently and then do nothing at calculation
  // time. Validate the shape here so the user sees it while typing: keyed maps
  // are `code|godMes` (optionally `|net`), the plain ones are just `godMes`.
  const KEY_PATTERNS = {
    godMes: /^\d{6}$/,
    codeGodMes: /^\d{2,4}\|\d{6}(\|net)?$/,
    code: /^\d{2,4}$/,
  };
  const kind = keyKind || (keyLabel ? 'codeGodMes' : 'godMes');
  const keyPattern = KEY_PATTERNS[kind] || KEY_PATTERNS.godMes;
  const isKeyValid = (k) => keyPattern.test(String(k || '').trim());
  const invalidRows = rows.filter((r) => r.godMes && !isKeyValid(r.godMes));
  const hasInvalid = invalidRows.length > 0;

  const handleSave = () => {
    if (hasInvalid) return;
    const out = {};
    for (const r of rows) {
      if (!r.godMes) continue;
      out[String(r.godMes).trim()] = Number(r.value) || 0;
    }
    onSave(out);
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      width={500}
      onOk={handleSave}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ disabled: hasInvalid }}
    >
      <div className="mb-3 flex justify-between text-xs text-gray-500">
        <span>{hint}</span>
        <Button size="small" icon={<PlusOutlined />} onClick={add}>
          {t('add')}
        </Button>
      </div>
      {hasInvalid && (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          message={
            kind === 'code'
              ? t('ozEarningCodeKeyInvalid')
              : kind === 'codeGodMes'
                ? t('ozAbFixedKeyInvalid')
                : t('ozGodMesKeyInvalid')
          }
        />
      )}
      <Table
        size="small"
        rowKey={(_, idx) => idx}
        dataSource={rows}
        pagination={false}
        columns={[
          {
            title: keyLabel || t('ozGodMesLabel'),
            dataIndex: 'godMes',
            width: keyLabel ? 180 : 140,
            render: (v, _, idx) => (
              <Input
                size="small"
                value={v}
                status={v && !isKeyValid(v) ? 'error' : undefined}
                placeholder={keyLabel ? '147|202606' : '202604'}
                onChange={(e) => update(idx, { godMes: e.target.value })}
              />
            ),
          },
          {
            title: valueLabel,
            dataIndex: 'value',
            render: (v, _, idx) => (
              <InputNumber
                size="small"
                value={v}
                min={0}
                step={0.1}
                onChange={(val) => update(idx, { value: val })}
                style={{ width: 100 }}
              />
            ),
          },
          {
            title: '',
            key: 'actions',
            width: 40,
            render: (_, __, idx) => (
              <Popconfirm
                title={t('confirmDelete')}
                onConfirm={() => remove(idx)}
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            ),
          },
        ]}
      />
    </Modal>
  );
}

function EmployeeDetailsExpand({ record, companyId, onSave, t }) {
  const [draft, setDraft] = useState({
    netTarget: record.netTarget ?? null,
    statusCode: record.statusCode ?? '',
    absoftMinNT: record.absoft?.minNT ?? null,
    absoftDpfPremium: record.absoft?.dpfPremium ?? null,
    otPremiumMultiplier: record.overtimePremium?.premiumMultiplier ?? null,
  });
  const [dirty, setDirty] = useState(false);
  const [deductionsOpen, setDeductionsOpen] = useState(false);
  const [procMrOpen, setProcMrOpen] = useState(false);
  const [unusedGoOpen, setUnusedGoOpen] = useState(false);
  const [rdHoursOpen, setRdHoursOpen] = useState(false);
  const [nonTaxOverrideOpen, setNonTaxOverrideOpen] = useState(false);
  const [longSickOpen, setLongSickOpen] = useState(false);
  const [dopunaOpen, setDopunaOpen] = useState(false);
  const [abFixedOpen, setAbFixedOpen] = useState(false);
  const [frozenRateOpen, setFrozenRateOpen] = useState(false);

  const set = (k, v) => {
    setDraft((prev) => ({ ...prev, [k]: v }));
    setDirty(true);
  };

  // Other income (outside employment) — service contract / author / supplementary / PP.
  const [regime, setRegime] = useState(record.incomeRegime || 'employment');
  const [oi, setOi] = useState({
    contractType: record.otherIncome?.contractType || 'ugovorODelu',
    authorClass: record.otherIncome?.authorClass || '50',
    insuredElsewhere: record.otherIncome?.insuredElsewhere ?? false,
    pioResolution: record.otherIncome?.pioResolution ?? false,
    personStatus: record.otherIncome?.personStatus || 'unemployed',
    amountIsNet: record.otherIncome?.amountIsNet ?? true,
    payoutMode: record.otherIncome?.payoutMode || 'monthly',
    defaultAmount: record.otherIncome?.defaultAmount ?? null,
  });
  const [oiDirty, setOiDirty] = useState(false);
  const setOiField = (k, v) => {
    setOi((prev) => ({ ...prev, [k]: v }));
    setOiDirty(true);
  };
  const saveOtherIncome = () => {
    if (regime !== 'otherIncome') {
      onSave({ incomeRegime: 'employment' });
    } else {
      onSave({ incomeRegime: 'otherIncome', otherIncome: { ...oi } });
    }
    setOiDirty(false);
  };

  // One-off other-income payout
  const now = dayjs();
  const [oneOffOpen, setOneOffOpen] = useState(false);
  const [oneOffBusy, setOneOffBusy] = useState(false);
  const [oneOff, setOneOff] = useState({
    month: now.month() + 1,
    year: now.year(),
    amount: null,
    amountIsNet: oi.amountIsNet,
  });
  const setOneOffField = (k, v) => setOneOff((p) => ({ ...p, [k]: v }));
  const submitOneOff = async () => {
    if (!oneOff.amount || oneOff.amount <= 0) {
      message.error('Unesi iznos veći od 0');
      return;
    }
    setOneOffBusy(true);
    try {
      const res = await ozService.oneOffOtherIncome({
        companyId,
        employeeId: record.employee,
        month: oneOff.month,
        year: oneOff.year,
        amount: oneOff.amount,
        amountIsNet: oneOff.amountIsNet,
        // if the profile is not yet saved as otherIncome, send the current choice
        otherIncome: regime === 'otherIncome' ? { ...oi } : undefined,
      });
      message.success(
        `Isplaćeno: OVP ${res.ovp}, bruto ${res.calculation?.totalGross?.toLocaleString('sr-RS')} RSD`
      );
      setOneOffOpen(false);
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    } finally {
      setOneOffBusy(false);
    }
  };

  return (
    <div className="bg-gray-50 p-3">
      <div className="grid grid-cols-4 gap-3">
        <div>
          <label className="mb-1 block text-xs text-gray-500">
            {t('ozNetTarget')}
          </label>
          <InputNumber
            size="small"
            className="w-full"
            value={draft.netTarget}
            min={0}
            onChange={(v) => set('netTarget', v)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">
            {t('ozStatusCodeDefault')}
          </label>
          <Input
            size="small"
            value={draft.statusCode}
            placeholder="010"
            onChange={(e) => set('statusCode', e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">
            {t('ozAbsoftMinNt')}
          </label>
          <InputNumber
            size="small"
            className="w-full"
            value={draft.absoftMinNT}
            min={0}
            onChange={(v) => set('absoftMinNT', v)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">
            DPF premija (mes. RSD)
          </label>
          <InputNumber
            size="small"
            className="w-full"
            value={draft.absoftDpfPremium}
            min={0}
            onChange={(v) => set('absoftDpfPremium', v)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">
            {t('ozProfileOtPremium')}
          </label>
          <InputNumber
            size="small"
            className="w-full"
            value={draft.otPremiumMultiplier}
            min={0}
            step={0.01}
            placeholder="npr. 1.26"
            onChange={(v) => set('otPremiumMultiplier', v)}
          />
        </div>
        <div className="flex items-end gap-2">
          <Button
            size="small"
            type="primary"
            disabled={!dirty}
            onClick={() =>
              onSave({
                netTarget: draft.netTarget,
                statusCode: draft.statusCode || undefined,
                absoft: {
                  ...(record.absoft || {}),
                  minNT: draft.absoftMinNT,
                  dpfPremium: draft.absoftDpfPremium ?? undefined,
                },
                overtimePremium: {
                  ...(record.overtimePremium || {}),
                  premiumMultiplier: draft.otPremiumMultiplier ?? null,
                },
              })
            }
          >
            {t('save')}
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => setDeductionsOpen(true)}
        >
          {t('ozActiveDeductions')}{' '}
          <Tag>{(record.activeDeductions || []).length}</Tag>
        </Button>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => setProcMrOpen(true)}
        >
          {t('ozAbsoftProcMr')}{' '}
          <Tag>{Object.keys(record.absoft?.procMR || {}).length}</Tag>
        </Button>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => setUnusedGoOpen(true)}
        >
          {t('ozUnusedGoHours')}{' '}
          <Tag>{Object.keys(record.absoft?.unusedGoHours || {}).length}</Tag>
        </Button>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => setRdHoursOpen(true)}
        >
          R&D sati (čl.21i){' '}
          <Tag>{Object.keys(record.absoft?.rdHours || {}).length}</Tag>
        </Button>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => setNonTaxOverrideOpen(true)}
        >
          {t('ozNonTaxableOverride')}{' '}
          <Tag>
            {Object.keys(record.absoft?.nonTaxableOverride || {}).length}
          </Tag>
        </Button>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => setLongSickOpen(true)}
        >
          {t('ozLongSickAmount')}{' '}
          <Tag>{Object.keys(record.absoft?.longSickAmount || {}).length}</Tag>
        </Button>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => setDopunaOpen(true)}
        >
          {t('ozDopunaAmount')}{' '}
          <Tag>{Object.keys(record.absoft?.dopunaAmount || {}).length}</Tag>
        </Button>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => setAbFixedOpen(true)}
        >
          {t('ozAbFixedAmounts')}{' '}
          <Tag>{Object.keys(record.absoft?.abFixedAmounts || {}).length}</Tag>
        </Button>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => setFrozenRateOpen(true)}
        >
          {t('ozFrozenHourlyRate')}{' '}
          <Tag>{Object.keys(record.absoft?.frozenHourlyRate || {}).length}</Tag>
        </Button>
      </div>

      {/* Other income (outside employment) — service contract / author / supplementary / PP */}
      <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3">
        <div className="mb-2 flex items-center gap-3">
          <span className="text-xs font-semibold text-amber-700">
            Drugi prihodi (van radnog odnosa)
          </span>
          <Switch
            size="small"
            checked={regime === 'otherIncome'}
            onChange={(checked) => {
              setRegime(checked ? 'otherIncome' : 'employment');
              setOiDirty(true);
            }}
            checkedChildren="Drugi prihod"
            unCheckedChildren="Zarada"
          />
          {regime === 'otherIncome' && (
            <span className="text-[11px] text-amber-600">
              Gase se: minuli rad, topli obrok/regres, godišnji, fond sati,
              doprinosi po zaradi.
            </span>
          )}
        </div>

        {regime === 'otherIncome' && (
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="mb-1 block text-xs text-gray-500">
                Vrsta ugovora
              </label>
              <Select
                size="small"
                className="w-full"
                value={oi.contractType}
                onChange={(v) => setOiField('contractType', v)}
                options={[
                  { value: 'ugovorODelu', label: 'Ugovor o delu' },
                  { value: 'autorski', label: 'Autorski ugovor' },
                  { value: 'dopunskiRad', label: 'Dopunski rad' },
                  { value: 'ppPoslovi', label: 'PP poslovi (nedokazano)' },
                ]}
              />
            </div>

            {oi.contractType === 'ugovorODelu' && (
              <>
                <div>
                  <label className="mb-1 block text-xs text-gray-500">
                    Osiguran po drugom osnovu
                  </label>
                  <Switch
                    size="small"
                    checked={oi.insuredElsewhere}
                    onChange={(v) => setOiField('insuredElsewhere', v)}
                    checkedChildren="Da"
                    unCheckedChildren="Ne"
                  />
                  <div className="mt-0.5 text-[10px] text-gray-400">
                    Ne → dodaje zdravstvo 10,3% (OVP 602)
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500">
                    PIO rešenje (bez doprinosa)
                  </label>
                  <Switch
                    size="small"
                    checked={oi.pioResolution}
                    onChange={(v) => setOiField('pioResolution', v)}
                    checkedChildren="Da"
                    unCheckedChildren="Ne"
                  />
                  <div className="mt-0.5 text-[10px] text-gray-400">
                    OVP 603
                  </div>
                </div>
              </>
            )}

            {oi.contractType === 'autorski' && (
              <div>
                <label className="mb-1 block text-xs text-gray-500">
                  Normirani troškovi
                </label>
                <Select
                  size="small"
                  className="w-full"
                  value={oi.authorClass}
                  onChange={(v) => setOiField('authorClass', v)}
                  options={[
                    { value: '50', label: '50%' },
                    { value: '43', label: '43%' },
                    { value: '34', label: '34%' },
                    { value: 'actual', label: 'Stvarni troškovi' },
                  ]}
                />
              </div>
            )}

            {oi.contractType === 'ppPoslovi' && (
              <div>
                <label className="mb-1 block text-xs text-gray-500">
                  Status lica
                </label>
                <Select
                  size="small"
                  className="w-full"
                  value={oi.personStatus}
                  onChange={(v) => setOiField('personStatus', v)}
                  options={[
                    { value: 'unemployed', label: 'Nezaposlen' },
                    { value: 'pensioner', label: 'Penzioner' },
                    { value: 'student', label: 'Student' },
                    { value: 'employedElsewhere', label: 'Zaposlen drugde' },
                  ]}
                />
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs text-gray-500">
                Iznos je
              </label>
              <Switch
                size="small"
                checked={oi.amountIsNet}
                onChange={(v) => setOiField('amountIsNet', v)}
                checkedChildren="Neto"
                unCheckedChildren="Bruto"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">
                Podrazumevani iznos
              </label>
              <InputNumber
                size="small"
                className="w-full"
                value={oi.defaultAmount}
                min={0}
                onChange={(v) => setOiField('defaultAmount', v)}
                placeholder="po isplati"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">
                Isplata
              </label>
              <Select
                size="small"
                className="w-full"
                value={oi.payoutMode}
                onChange={(v) => setOiField('payoutMode', v)}
                options={[
                  { value: 'monthly', label: 'Mesečno' },
                  { value: 'oneoff', label: 'Jednokratno' },
                ]}
              />
            </div>
          </div>
        )}

        <div className="mt-2 flex gap-2">
          <Button
            size="small"
            type="primary"
            disabled={!oiDirty}
            onClick={saveOtherIncome}
          >
            Sačuvaj režim prihoda
          </Button>
          {regime === 'otherIncome' && (
            <Button
              size="small"
              icon={<ThunderboltOutlined />}
              onClick={() => setOneOffOpen(true)}
            >
              Jednokratna isplata
            </Button>
          )}
        </div>
      </div>

      <Modal
        title="Jednokratna isplata drugog prihoda"
        open={oneOffOpen}
        onCancel={() => setOneOffOpen(false)}
        confirmLoading={oneOffBusy}
        okText="Isplati"
        cancelText="Otkaži"
        onOk={submitOneOff}
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-gray-500">Mesec</label>
            <InputNumber
              className="w-full"
              min={1}
              max={12}
              value={oneOff.month}
              onChange={(v) => setOneOffField('month', v)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">Godina</label>
            <InputNumber
              className="w-full"
              min={2014}
              max={2100}
              value={oneOff.year}
              onChange={(v) => setOneOffField('year', v)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">
              Iznos (RSD)
            </label>
            <InputNumber
              className="w-full"
              min={0}
              value={oneOff.amount}
              onChange={(v) => setOneOffField('amount', v)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">Iznos je</label>
            <Switch
              checked={oneOff.amountIsNet}
              onChange={(v) => setOneOffField('amountIsNet', v)}
              checkedChildren="Neto"
              unCheckedChildren="Bruto"
            />
          </div>
        </div>
        <div className="mt-2 text-[11px] text-gray-400">
          Kreira/dopunjava &bdquo;Drugi prihodi&rdquo; obračun (OTHER_INCOME) za
          izabrani mesec. Sačuvaj režim prihoda pre isplate ako menjaš tip
          ugovora.
        </div>
      </Modal>

      <ActiveDeductionsModal
        open={deductionsOpen}
        onClose={() => setDeductionsOpen(false)}
        value={record.activeDeductions || []}
        onSave={(rows) => {
          onSave({ activeDeductions: rows });
          setDeductionsOpen(false);
        }}
        t={t}
      />
      <AbsoftGodMesMapModal
        open={procMrOpen}
        onClose={() => setProcMrOpen(false)}
        value={record.absoft?.procMR || {}}
        onSave={(map) => {
          onSave({
            absoft: { ...(record.absoft || {}), procMR: map },
          });
          setProcMrOpen(false);
        }}
        t={t}
        title={t('ozAbsoftProcMr')}
        hint={t('ozAbsoftProcMrHint')}
        valueLabel="%"
      />
      <AbsoftGodMesMapModal
        open={unusedGoOpen}
        onClose={() => setUnusedGoOpen(false)}
        value={record.absoft?.unusedGoHours || {}}
        onSave={(map) => {
          onSave({
            absoft: { ...(record.absoft || {}), unusedGoHours: map },
          });
          setUnusedGoOpen(false);
        }}
        t={t}
        title={t('ozUnusedGoHours')}
        hint={t('ozUnusedGoHoursHint')}
        valueLabel="h"
      />
      <AbsoftGodMesMapModal
        open={rdHoursOpen}
        onClose={() => setRdHoursOpen(false)}
        value={record.absoft?.rdHours || {}}
        onSave={(map) => {
          onSave({
            absoft: { ...(record.absoft || {}), rdHours: map },
          });
          setRdHoursOpen(false);
        }}
        t={t}
        title="R&D sati (čl. 21i)"
        hint="Sati R&D angažovanja po mesecu (YYYYMM → sati). Engine deli 100/101/102 → 199/200/201."
        valueLabel="h"
      />
      <AbsoftGodMesMapModal
        open={nonTaxOverrideOpen}
        onClose={() => setNonTaxOverrideOpen(false)}
        value={record.absoft?.nonTaxableOverride || {}}
        onSave={(map) => {
          onSave({
            absoft: { ...(record.absoft || {}), nonTaxableOverride: map },
          });
          setNonTaxOverrideOpen(false);
        }}
        t={t}
        title={t('ozNonTaxableOverride')}
        hint={t('ozNonTaxableOverrideHint')}
        valueLabel="RSD"
      />
      <AbsoftGodMesMapModal
        open={longSickOpen}
        onClose={() => setLongSickOpen(false)}
        value={record.absoft?.longSickAmount || {}}
        onSave={(map) => {
          onSave({
            absoft: { ...(record.absoft || {}), longSickAmount: map },
          });
          setLongSickOpen(false);
        }}
        t={t}
        title={t('ozLongSickAmount')}
        hint={t('ozLongSickAmountHint')}
        valueLabel="RSD"
      />
      <AbsoftGodMesMapModal
        open={dopunaOpen}
        onClose={() => setDopunaOpen(false)}
        value={record.absoft?.dopunaAmount || {}}
        onSave={(map) => {
          onSave({
            absoft: { ...(record.absoft || {}), dopunaAmount: map },
          });
          setDopunaOpen(false);
        }}
        t={t}
        title={t('ozDopunaAmount')}
        hint={t('ozDopunaAmountHint')}
        valueLabel="RSD"
      />
      <AbsoftGodMesMapModal
        open={abFixedOpen}
        onClose={() => setAbFixedOpen(false)}
        value={record.absoft?.abFixedAmounts || {}}
        onSave={(map) => {
          onSave({
            absoft: { ...(record.absoft || {}), abFixedAmounts: map },
          });
          setAbFixedOpen(false);
        }}
        t={t}
        title={t('ozAbFixedAmounts')}
        hint={t('ozAbFixedAmountsHint')}
        keyLabel={t('ozAbFixedKey')}
        keyKind="codeGodMes"
        valueLabel="RSD"
      />
      <AbsoftGodMesMapModal
        open={frozenRateOpen}
        onClose={() => setFrozenRateOpen(false)}
        value={record.absoft?.frozenHourlyRate || {}}
        onSave={(map) => {
          onSave({
            absoft: { ...(record.absoft || {}), frozenHourlyRate: map },
          });
          setFrozenRateOpen(false);
        }}
        t={t}
        title={t('ozFrozenHourlyRate')}
        hint={t('ozFrozenHourlyRateHint')}
        keyLabel={t('ozFrozenHourlyRateKey')}
        keyKind="code"
        valueLabel="RSD/h"
      />
    </div>
  );
}

export default function OzEmployeeProfiles({
  companyId,
  focusEmployeeId = null,
  onClearFocus,
}) {
  const { t } = useTranslation(['forms']);
  const { data: profiles, isLoading } = useEmployeeProfiles(companyId);
  const updateProfile = useUpdateEmployeeProfile();

  const calcTypeOptions = useMemo(
    () =>
      CALC_TYPE_KEYS.map((k) => ({
        value: k.value,
        label: t(k.key),
      })),
    [t]
  );

  const bulkUpdate = useBulkUpdateProfiles();

  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [search, setSearch] = useState('');
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);
  // Status timeline modal state
  const [timelineEmp, setTimelineEmp] = useState(null);

  // Bulk update state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkField, setBulkField] = useState('fixedGross');
  const [bulkMode, setBulkMode] = useState('percent');
  const [bulkValue, setBulkValue] = useState(5);
  const [bulkSelected, setBulkSelected] = useState([]);

  // Test-run (dry-run) state
  const [testRunOpen, setTestRunOpen] = useState(false);
  const [testRunSelected, setTestRunSelected] = useState([]);
  const [testRunMonth, setTestRunMonth] = useState(dayjs());
  const [testRunExchange, setTestRunExchange] = useState(null);
  const [testRunBusy, setTestRunBusy] = useState(false);
  const [testRunResult, setTestRunResult] = useState(null);

  const runDryRun = async () => {
    if (!testRunSelected.length) {
      message.warning(t('selectCompany')); // fallback msg
      return;
    }
    setTestRunBusy(true);
    setTestRunResult(null);
    try {
      const data = await ozService.dryRunPayroll({
        companyId,
        month: testRunMonth.month() + 1,
        year: testRunMonth.year(),
        employeeIds: testRunSelected,
        exchangeRate: testRunExchange,
      });
      setTestRunResult(data);
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    } finally {
      setTestRunBusy(false);
    }
  };

  const filtered = useMemo(() => {
    if (!profiles) return [];
    let out = profiles;
    // Opened from an employee profile: show only that employee.
    if (focusEmployeeId) {
      out = out.filter((p) => String(p.employee) === String(focusEmployeeId));
    }
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(
        (p) =>
          `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
          p.jmbg?.includes(q) ||
          p.earningCode?.toLowerCase().includes(q) ||
          p.calcType?.toLowerCase().includes(q) ||
          p.sectorCode?.toLowerCase().includes(q)
      );
    }
    if (showIncompleteOnly) {
      out = out.filter((p) => !profileCompleteness(p).ok);
    }
    return out;
  }, [profiles, search, showIncompleteOnly, focusEmployeeId]);

  const summary = useMemo(() => {
    if (!profiles) return { total: 0, complete: 0, incomplete: 0 };
    let complete = 0;
    for (const p of profiles) {
      if (profileCompleteness(p).ok) complete += 1;
    }
    return {
      total: profiles.length,
      complete,
      incomplete: profiles.length - complete,
    };
  }, [profiles]);

  const startEdit = (record) => {
    setEditingId(record._id);
    setEditValues({
      calcType: record.calcType,
      points: record.points,
      fixedGross: record.fixedGross,
      fixedNet: record.fixedNet,
      earningCode: record.earningCode,
      seniorityYears: record.seniority?.years ?? 0,
      seniorityRate: record.seniority?.ratePerYear ?? 0.004,
      incentive1: record.incentive1 ?? false,
      incentive2: record.incentive2 ?? false,
      sectorCode: record.sectorCode ?? '',
      workTimePercent: record.workTimePercent ?? 100,
      contractAvgGross: record.contractAvgGross ?? null,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const saveEdit = async (record) => {
    try {
      await updateProfile.mutateAsync({
        employeeId: record.employee,
        profile: {
          company: companyId,
          calcType: editValues.calcType,
          points: editValues.points,
          fixedGross: editValues.fixedGross,
          fixedNet: editValues.fixedNet,
          earningCode: editValues.earningCode,
          seniority: {
            years: editValues.seniorityYears,
            ratePerYear: editValues.seniorityRate,
          },
          incentive1: editValues.incentive1,
          incentive2: editValues.incentive2,
          sectorCode: editValues.sectorCode || undefined,
          workTimePercent: editValues.workTimePercent,
          contractAvgGross: editValues.contractAvgGross,
        },
      });
      message.success(t('saved'));
      setEditingId(null);
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    }
  };

  // Save patch from the expand-row form (does NOT require row-edit mode)
  const saveExpandPatch = async (record, patch) => {
    try {
      await updateProfile.mutateAsync({
        employeeId: record.employee,
        profile: {
          company: companyId,
          ...patch,
        },
      });
      message.success(t('saved'));
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    }
  };

  const openBulk = useCallback(() => {
    setBulkSelected((profiles || []).map((p) => p.employee));
    setBulkOpen(true);
  }, [profiles]);

  const bulkPreview = useMemo(() => {
    if (!profiles) return [];
    return profiles
      .filter((p) => bulkSelected.includes(p.employee))
      .map((p) => {
        const current = p[bulkField] || 0;
        let next;
        if (bulkMode === 'percent') {
          next = Math.round(current * (1 + bulkValue / 100));
        } else if (bulkMode === 'fixed_add') {
          next = Math.round(current + bulkValue);
        } else {
          next = bulkValue;
        }
        return {
          employee: p.employee,
          name: `${p.lastName} ${p.firstName}`,
          current,
          next,
        };
      });
  }, [profiles, bulkSelected, bulkField, bulkMode, bulkValue]);

  const handleBulkApply = async () => {
    try {
      const result = await bulkUpdate.mutateAsync({
        companyId,
        employeeIds: bulkSelected,
        update: {
          mode: bulkMode,
          field: bulkField,
          value: bulkValue,
        },
      });
      message.success(`${t('ozBulkUpdated')}: ${result.updated}`);
      setBulkOpen(false);
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    }
  };

  const setField = (key, val) =>
    setEditValues((prev) => ({ ...prev, [key]: val }));

  const columns = [
    {
      title: '',
      key: 'complete',
      width: 28,
      render: (_, r) => {
        const c = profileCompleteness(r);
        if (c.ok) return <Tag color="green">✓</Tag>;
        return (
          <Tooltip title={`${t('ozProfileMissing')}: ${c.missing.join(', ')}`}>
            <WarningOutlined style={{ color: '#faad14' }} />
          </Tooltip>
        );
      },
    },
    {
      title: t('employee'),
      key: 'employee',
      render: (_, r) => {
        const name = `${r.lastName || ''} ${r.firstName || ''}`.trim() || '-';
        const url = `${ROUTES.admin}/${ROUTES.employees}/${OPERATION.view}/${r.employee}`;
        return (
          <span className="inline-flex items-center gap-1.5">
            <span className="text-sm">{name}</span>
            <Link to={url}>
              <LinkOutlined className="text-xs text-primary hover:opacity-75" />
            </Link>
            {r.incomeRegime === 'otherIncome' && (
              <Tag color="orange" className="!ml-1 !text-[10px]">
                Drugi prihod
              </Tag>
            )}
          </span>
        );
      },
      width: 180,
    },
    {
      title: t('ozCalcType'),
      dataIndex: 'calcType',
      key: 'calcType',
      width: 180,
      render: (v, record) =>
        editingId === record._id ? (
          <Select
            size="small"
            style={{ width: 170 }}
            value={editValues.calcType}
            onChange={(val) => setField('calcType', val)}
            options={calcTypeOptions}
          />
        ) : (
          <Tag>{calcTypeOptions.find((o) => o.value === v)?.label || v}</Tag>
        ),
    },
    {
      title: t('ozSectorCode'),
      dataIndex: 'sectorCode',
      key: 'sectorCode',
      width: 90,
      render: (v, record) =>
        editingId === record._id ? (
          <Input
            size="small"
            value={editValues.sectorCode}
            onChange={(e) => setField('sectorCode', e.target.value)}
            placeholder="01"
            style={{ width: 70 }}
          />
        ) : (
          v || '-'
        ),
    },
    {
      title: t('ozWorkTimePercent'),
      dataIndex: 'workTimePercent',
      key: 'workTimePercent',
      width: 100,
      render: (v, record) =>
        editingId === record._id ? (
          <Select
            size="small"
            style={{ width: 80 }}
            value={editValues.workTimePercent}
            onChange={(val) => setField('workTimePercent', val)}
            options={WORK_TIME_OPTIONS}
          />
        ) : v != null ? (
          `${v}%`
        ) : (
          '100%'
        ),
    },
    {
      title: t('ozPoints'),
      dataIndex: 'points',
      key: 'points',
      width: 100,
      render: (v, record) =>
        editingId === record._id ? (
          <InputNumber
            size="small"
            value={editValues.points}
            onChange={(val) => setField('points', val)}
            min={0}
            step={0.01}
            style={{ width: 90 }}
          />
        ) : (
          v?.toLocaleString('sr-RS') || '-'
        ),
    },
    {
      title: t('ozFixedGross'),
      dataIndex: 'fixedGross',
      key: 'fixedGross',
      width: 110,
      render: (v, record) =>
        editingId === record._id ? (
          <InputNumber
            size="small"
            value={editValues.fixedGross}
            onChange={(val) => setField('fixedGross', val)}
            min={0}
            style={{ width: 100 }}
          />
        ) : (
          v?.toLocaleString('sr-RS') || '-'
        ),
    },
    {
      title: t('ozFixedNet'),
      dataIndex: 'fixedNet',
      key: 'fixedNet',
      width: 110,
      render: (v, record) =>
        editingId === record._id ? (
          <InputNumber
            size="small"
            value={editValues.fixedNet}
            onChange={(val) => setField('fixedNet', val)}
            min={0}
            style={{ width: 100 }}
          />
        ) : (
          v?.toLocaleString('sr-RS') || '-'
        ),
    },
    {
      title: t('ozContractAvgGross'),
      dataIndex: 'contractAvgGross',
      key: 'contractAvgGross',
      width: 130,
      render: (v, record) =>
        editingId === record._id ? (
          <InputNumber
            size="small"
            value={editValues.contractAvgGross}
            onChange={(val) => setField('contractAvgGross', val)}
            min={0}
            style={{ width: 120 }}
          />
        ) : (
          v?.toLocaleString('sr-RS') || '-'
        ),
    },
    {
      title: t('ozSeniorityYears'),
      dataIndex: ['seniority', 'years'],
      key: 'seniorityYears',
      width: 80,
      render: (v, record) =>
        editingId === record._id ? (
          <InputNumber
            size="small"
            value={editValues.seniorityYears}
            onChange={(val) => setField('seniorityYears', val)}
            min={0}
            max={50}
            style={{ width: 60 }}
          />
        ) : (
          (v ?? 0)
        ),
    },
    {
      title: t('ozSeniorityRate'),
      dataIndex: ['seniority', 'ratePerYear'],
      key: 'seniorityRate',
      width: 80,
      render: (v, record) =>
        editingId === record._id ? (
          <InputNumber
            size="small"
            value={editValues.seniorityRate}
            onChange={(val) => setField('seniorityRate', val)}
            min={0}
            max={0.05}
            step={0.001}
            style={{ width: 70 }}
          />
        ) : (
          `${((v ?? 0.004) * 100).toFixed(1)}%`
        ),
    },
    {
      title: t('earningCode'),
      dataIndex: 'earningCode',
      key: 'earningCode',
      width: 90,
      render: (v, record) =>
        editingId === record._id ? (
          <Input
            size="small"
            value={editValues.earningCode}
            onChange={(e) => setField('earningCode', e.target.value)}
            style={{ width: 80 }}
          />
        ) : (
          v || '-'
        ),
    },
    {
      title: t('ozIncentive1'),
      dataIndex: 'incentive1',
      key: 'incentive1',
      width: 60,
      render: (v, record) =>
        editingId === record._id ? (
          <Switch
            size="small"
            checked={editValues.incentive1}
            onChange={(val) => setField('incentive1', val)}
          />
        ) : v ? (
          <Tag color="green">{t('yes')}</Tag>
        ) : (
          ''
        ),
    },
    {
      title: t('ozIncentive2'),
      dataIndex: 'incentive2',
      key: 'incentive2',
      width: 60,
      render: (v, record) =>
        editingId === record._id ? (
          <Switch
            size="small"
            checked={editValues.incentive2}
            onChange={(val) => setField('incentive2', val)}
          />
        ) : v ? (
          <Tag color="blue">{t('yes')}</Tag>
        ) : (
          ''
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, record) =>
        editingId === record._id ? (
          <Space>
            <Button
              size="small"
              type="primary"
              icon={<SaveOutlined />}
              loading={updateProfile.isPending}
              onClick={() => saveEdit(record)}
            />
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={cancelEdit}
            />
          </Space>
        ) : (
          <Space size="small">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => startEdit(record)}
              disabled={editingId !== null}
            />
            <Button
              size="small"
              icon={<CalendarOutlined />}
              onClick={() =>
                setTimelineEmp({
                  id: record.employee || record._id,
                  name: `${record.lastName || ''} ${record.firstName || ''}`.trim(),
                })
              }
              disabled={editingId !== null}
              title={t('ozStatusTimeline') || 'Status timeline'}
            />
          </Space>
        ),
    },
  ];

  if (!companyId) {
    return (
      <div className="p-8 text-center text-gray-400">{t('selectCompany')}</div>
    );
  }
  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {summary.total} {t('employees').toLowerCase()}
          </span>
          <Tag color="green">
            {t('ozProfileComplete')}: {summary.complete}
          </Tag>
          <Tag color={summary.incomplete > 0 ? 'orange' : 'default'}>
            {t('ozProfileIncomplete')}: {summary.incomplete}
          </Tag>
          {focusEmployeeId && (
            <Tag
              color="blue"
              closable
              onClose={(e) => {
                e.preventDefault();
                onClearFocus?.();
              }}
            >
              {t('ozShowingOneEmployee')}
            </Tag>
          )}
          <Checkbox
            checked={showIncompleteOnly}
            onChange={(e) => setShowIncompleteOnly(e.target.checked)}
          >
            <span className="text-xs">{t('ozShowIncompleteOnly')}</span>
          </Checkbox>
          <Button
            size="small"
            icon={<ThunderboltOutlined />}
            className="btn-secondary !rounded-[50px]"
            onClick={openBulk}
          >
            {t('ozBulkUpdate')}
          </Button>
          <Button
            size="small"
            icon={<ExperimentOutlined />}
            onClick={() => {
              setTestRunSelected([]);
              setTestRunResult(null);
              setTestRunOpen(true);
            }}
          >
            {t('ozTestRun')}
          </Button>
        </div>
        <Input.Search
          size="small"
          placeholder={t('search')}
          style={{ width: 250 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
      </div>
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="_id"
        size="small"
        pagination={{
          pageSize: 20,
          showSizeChanger: false,
          size: 'small',
        }}
        scroll={{ x: 1400 }}
        expandable={{
          expandedRowRender: (record) => (
            <EmployeeDetailsExpand
              record={record}
              companyId={companyId}
              onSave={(patch) => saveExpandPatch(record, patch)}
              t={t}
            />
          ),
          rowExpandable: () => true,
          // The focused employee's row opens on arrival; the user can still
          // collapse it like any other row.
          ...(focusEmployeeId && {
            defaultExpandedRowKeys: filtered.map((p) => p._id),
          }),
        }}
      />

      <Modal
        title={t('ozBulkUpdate')}
        open={bulkOpen}
        onCancel={() => setBulkOpen(false)}
        width={650}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              className="btn-default btn-default--hover"
              onClick={() => setBulkOpen(false)}
            >
              {t('cancel')}
            </Button>
            <Button
              className="btn-primary"
              loading={bulkUpdate.isPending}
              disabled={!bulkSelected.length}
              onClick={handleBulkApply}
            >
              {t('ozBulkApply')}
            </Button>
          </div>
        }
      >
        <div className="mb-4 grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-xs text-gray-500">
              {t('ozBulkField')}
            </label>
            <Select
              size="small"
              className="w-full"
              value={bulkField}
              onChange={setBulkField}
              options={[
                { value: 'fixedGross', label: t('ozFixedGross') },
                { value: 'fixedNet', label: t('ozFixedNet') },
                { value: 'points', label: t('ozPoints') },
                { value: 'contractAvgGross', label: t('ozContractAvgGross') },
                { value: 'netTarget', label: t('ozNetTarget') },
                { value: 'workTimePercent', label: t('ozWorkTimePercent') },
              ]}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">
              {t('ozBulkMode')}
            </label>
            <Select
              size="small"
              className="w-full"
              value={bulkMode}
              onChange={setBulkMode}
              options={[
                { value: 'percent', label: t('ozBulkPercent') },
                { value: 'fixed_set', label: t('ozBulkFixedSet') },
                { value: 'fixed_add', label: t('ozBulkFixedAdd') },
              ]}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">
              {t('ozBulkValue')}
            </label>
            <InputNumber
              size="small"
              className="w-full"
              value={bulkValue}
              onChange={setBulkValue}
            />
          </div>
        </div>

        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-500">
            {t('ozBulkPreview')} ({bulkSelected.length})
          </span>
          <Checkbox
            checked={bulkSelected.length === (profiles?.length || 0)}
            onChange={(e) =>
              setBulkSelected(
                e.target.checked ? (profiles || []).map((p) => p.employee) : []
              )
            }
          >
            <span className="text-xs">{t('ozBulkSelectAll')}</span>
          </Checkbox>
        </div>

        <Table
          dataSource={bulkPreview}
          rowKey="employee"
          size="small"
          pagination={false}
          scroll={{ y: 300 }}
          columns={[
            {
              title: t('employee'),
              dataIndex: 'name',
              width: 200,
            },
            {
              title: t('ozBulkPrevious'),
              dataIndex: 'current',
              render: (v) => v?.toLocaleString('sr-RS') || '0',
              width: 120,
            },
            {
              title: t('ozBulkNew'),
              dataIndex: 'next',
              render: (v) => v?.toLocaleString('sr-RS') || '0',
              width: 120,
            },
            {
              title: '',
              key: 'select',
              width: 40,
              render: (_, r) => (
                <Checkbox
                  checked={bulkSelected.includes(r.employee)}
                  onChange={(e) =>
                    setBulkSelected((prev) =>
                      e.target.checked
                        ? [...prev, r.employee]
                        : prev.filter((id) => id !== r.employee)
                    )
                  }
                />
              ),
            },
          ]}
        />
      </Modal>

      <StatusTimelineModal
        open={!!timelineEmp}
        onClose={() => setTimelineEmp(null)}
        employeeId={timelineEmp?.id}
        companyId={companyId}
        employeeName={timelineEmp?.name}
      />

      <Modal
        title={t('ozTestRun')}
        open={testRunOpen}
        onCancel={() => setTestRunOpen(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setTestRunOpen(false)}>
            {t('cancel')}
          </Button>,
          <Button
            key="run"
            type="primary"
            loading={testRunBusy}
            disabled={!testRunSelected.length}
            onClick={runDryRun}
          >
            {t('ozTestRun')}
          </Button>,
        ]}
      >
        <Alert
          type="info"
          message={t('ozTestRunHint')}
          className="mb-3"
          showIcon
        />
        <div className="mb-3 grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-xs text-gray-500">
              {t('month')}
            </label>
            <DatePicker
              picker="month"
              value={testRunMonth}
              onChange={(v) => setTestRunMonth(v || dayjs())}
              allowClear={false}
              format="MMMM YYYY"
              className="w-full"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">
              {t('ozEurRate')}
            </label>
            <InputNumber
              value={testRunExchange}
              onChange={setTestRunExchange}
              precision={4}
              min={0}
              step={0.0001}
              className="w-full"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">
              {t('employees')}: {testRunSelected.length} / 3 max
            </label>
          </div>
        </div>

        <Table
          size="small"
          rowKey="employee"
          dataSource={(profiles || []).slice(0, 100)}
          pagination={{ pageSize: 10 }}
          rowSelection={{
            selectedRowKeys: testRunSelected,
            onChange: (keys) => {
              if (keys.length > 3) {
                message.warning(t('ozTestRunMaxThree'));
                return;
              }
              setTestRunSelected(keys);
            },
          }}
          columns={[
            {
              title: t('employee'),
              render: (_, r) => `${r.lastName} ${r.firstName}`,
            },
            {
              title: t('ozCalcType'),
              dataIndex: 'calcType',
              render: (v) =>
                calcTypeOptions.find((o) => o.value === v)?.label || v,
            },
          ]}
        />

        {testRunResult && (
          <div className="mt-4">
            <h4 className="mb-2 font-semibold">
              {t('ozTestRunResults')} ({testRunResult.employeesProcessed})
            </h4>
            <Table
              size="small"
              rowKey="employeeId"
              dataSource={testRunResult.results}
              pagination={false}
              columns={[
                { title: t('employee'), dataIndex: 'name' },
                {
                  title: t('gross'),
                  dataIndex: ['calculation', 'totalGross'],
                  render: (v) => v?.toLocaleString('sr-RS') || '-',
                },
                {
                  title: t('net'),
                  dataIndex: ['calculation', 'netSalary'],
                  render: (v) => v?.toLocaleString('sr-RS') || '-',
                },
                {
                  title: t('tax'),
                  dataIndex: ['calculation', 'tax'],
                  render: (v) => v?.toLocaleString('sr-RS') || '-',
                },
                {
                  title: t('status'),
                  dataIndex: 'status',
                  render: (v) =>
                    v === 'OK' ? (
                      <Tag color="green">{v}</Tag>
                    ) : (
                      <Tag color="red">{v}</Tag>
                    ),
                },
              ]}
              expandable={{
                expandedRowRender: (r) => (
                  <Table
                    size="small"
                    rowKey={(c, i) => `${r.employeeId}-${i}`}
                    dataSource={r.components || []}
                    pagination={false}
                    columns={[
                      { title: t('code'), dataIndex: 'earningCode', width: 80 },
                      { title: t('name'), dataIndex: 'name' },
                      {
                        title: t('workType'),
                        dataIndex: 'workType',
                        width: 130,
                      },
                      {
                        title: t('ozAmount'),
                        dataIndex: 'amount',
                        render: (v) => v?.toLocaleString('sr-RS'),
                      },
                    ]}
                  />
                ),
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
